/**
 * Tasks, slips, blocks, proof of closure, change requests and dropped items — ADR-001 §7.2, §7.6, §11.4,
 * guide §6.2, operator decision 12 (a closer never checks their own proof; if the associate director
 * closed it, the founder checks).
 *
 * State machine (ADR §11.4): there is no path from open to finished that skips proof, and no path to
 * dropped that skips a reason.
 *
 * Fixed reason lists use the prototype's exact wording (docs/prototype.html SLIP_REASONS, CHG_REASONS,
 * REASONS) — the approved design for copy. Codes are stable; labels are what the UI prints.
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, Version, list, page } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
// ---------------------------------------------------------------- enums + reason lists
export const TaskPriority = z.enum(['urgent', 'high', 'medium', 'low']).meta({ id: 'TaskPriority' });
/** Where the task came from. `history` = the history importer (ADR §10.1); `engage` = "Add what is due" (ADR §8.7). */
export const TaskSource = z
    .enum(['checklist', 'voice', 'mom', 'request', 'email', 'adhoc', 'history', 'engage'])
    .meta({ id: 'TaskSource' });
/** Prototype `SRC` labels. */
export const TASK_SOURCE_LABEL = {
    voice: 'Voice note',
    mom: 'Meeting minutes',
    checklist: 'Checklist',
    adhoc: 'Ad hoc',
    request: 'Client request',
    email: 'Email',
    history: 'Imported history',
    engage: 'Engagement',
};
/** The associate director's ordering for the day plan: Do first / Do next / Later (ADR §7.6). */
export const HeadPriority = z.enum(['first', 'next', 'later']).meta({ id: 'HeadPriority' });
export const HEAD_PRIORITY_LABEL = { first: 'Do first', next: 'Do next', later: 'Later' };
const reasonList = (id, codes) => z.enum(codes).meta({ id });
/** Prototype SLIP_REASONS — why a due date moved later. `other` requires a note. */
export const SlipReason = reasonList('SlipReason', [
    'waiting_on_client',
    'waiting_on_someone_else',
    'higher_priority_work',
    'took_longer',
    'other',
]);
export const SLIP_REASON_LABEL = {
    waiting_on_client: 'Waiting on the client',
    waiting_on_someone_else: 'Waiting on someone else',
    higher_priority_work: 'Higher priority work came up',
    took_longer: 'Took longer than I thought',
    other: 'Other',
};
/** Prototype CHG_REASONS — why a member asks to move or drop a task. `other` requires a note. */
export const ChangeReason = reasonList('ChangeReason', [
    'someone_else_doing_it',
    'not_my_client',
    'need_help',
    'other',
]);
export const CHANGE_REASON_LABEL = {
    someone_else_doing_it: 'Someone else is already doing this',
    not_my_client: 'This is not for my client',
    need_help: 'I need help, I have too much on',
    other: 'Other',
};
/** Prototype REASONS — why an item was left out on purpose (Dropped tasks). `other` requires a note. */
export const DropReason = reasonList('DropReason', [
    'duplicate',
    'already_done',
    'not_needed',
    'clients_job',
    'wrong_client_or_person',
    'too_vague',
    'other',
]);
export const DROP_REASON_LABEL = {
    duplicate: 'Duplicate',
    already_done: 'Already done',
    not_needed: 'Not needed',
    clients_job: 'Client’s job, not ours',
    wrong_client_or_person: 'Wrong client or person',
    too_vague: 'Too vague to act on',
    other: 'Other',
};
/**
 * The MIS rule (prototype `isMisT`, ADR §8.7): a task whose title contains the word "MIS" — and is not
 * about reconciling, requesting or formatting it — needs a `next_step` when it is closed ("A report on its
 * own is a tick-mark").
 */
export const isMisTask = (title) => /\bMIS\b/.test(title) && !/reconcil|request|format/i.test(title);
/** Slips at which a task is flagged to the associate director (guide §6.2). */
export const SLIP_FLAG_AT = 3;
/**
 * Default of settings `task.block_flag_days`. A task is flagged when blocked MORE THAN this many whole days
 * (guide §6.2/§6.4): exactly 3 = not flagged, 4 = flagged. The server reads the live value from Settings.
 */
export const BLOCK_FLAG_DAYS = 3;
const Note = z.string().trim().min(1).max(1000);
const needsNoteForOther = (b, ctx) => {
    if (b.reason_code === 'other' && !b.note) {
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say what the reason is when you pick Other' });
    }
};
// ---------------------------------------------------------------- task parts
export const Slip = z
    .object({
    id: Uuid,
    from_due: IsoDate,
    to_due: IsoDate,
    reason_code: SlipReason,
    note: z.string().nullable(),
    by: UserRef,
    at: Timestamp,
})
    .meta({ id: 'Slip' });
export const TaskBlock = z
    .object({
    /** Free text: the person or party it waits on. */
    on: z.string(),
    since: IsoDate,
    /** Whole days blocked so far; flagged when more than settings `task.block_flag_days` (default 3). */
    days: z.number().int().nonnegative(),
    note: z.string().nullable(),
    flagged: z.boolean(),
})
    .meta({ id: 'TaskBlock' });
export const CheckState = z.enum(['not_checked', 'checked', 'rejected']).meta({ id: 'CheckState' });
export const Closure = z
    .object({
    proof_kind: z.enum(['note', 'link']),
    proof_text: z.string().nullable(),
    proof_url: z.string().nullable(),
    /** Object-storage key of an attached file, when one was uploaded. */
    proof_file_key: z.string().nullable(),
    no_attachment_needed: z.boolean(),
    /** Required for MIS tasks: what we suggest next. */
    next_step: z.string().nullable(),
    closed_by: UserRef,
    /** Set when the closer was acting as someone else (operator decision 18). */
    closed_by_on_behalf_of: UserRef.nullable(),
    closed_at: Timestamp,
    check_state: CheckState,
    checked_by: UserRef.nullable(),
    checked_at: Timestamp.nullable(),
    reject_reason: z.string().nullable(),
    /**
     * The closer's note to the requester (internal requests: "{owner} says: {note}" on the confirm panel).
     * Always `null` on tasks, client requests and escalations, whose close has no such note.
     */
    note: z.string().nullable(),
})
    .meta({ id: 'Closure' });
export const ChangeRequestStatus = z.enum(['waiting', 'approved', 'declined']).meta({ id: 'ChangeRequestStatus' });
export const ChangeDecision = z.enum(['reassign', 'remove', 'keep']).meta({ id: 'ChangeDecision' });
export const ChangeRequest = z
    .object({
    id: Uuid,
    task_id: Uuid,
    task_title: z.string(),
    client: ClientRef.nullable(),
    by: UserRef,
    reason_code: ChangeReason,
    note: z.string().nullable(),
    suggested_user: UserRef.nullable(),
    suggested_due: IsoDate.nullable(),
    status: ChangeRequestStatus,
    decision: ChangeDecision.nullable(),
    decided_by: UserRef.nullable(),
    decided_at: Timestamp.nullable(),
    decision_note: z.string().nullable(),
    created_at: Timestamp,
})
    .meta({ id: 'ChangeRequest' });
/**
 * What the task came from (held item, meeting, request, checklist item…), with a label and SPA route. `email_thread`:
 * "Make task" on an email thread (the thread is the origin; a confirmed proposal is `proposed_item`).
 */
export const OriginRef = z
    .object({
    kind: z.enum(['held_item', 'meeting', 'client_request', 'internal_request', 'client_task', 'checklist_item', 'routine', 'proposed_item', 'dropped_item', 'import', 'engagement', 'email_thread']),
    id: z.string(),
    label: z.string(),
    route: z.string().nullable(),
})
    .meta({ id: 'OriginRef' });
// ---------------------------------------------------------------- Task
export const TaskState = z.enum(['open', 'blocked', 'closed_unchecked', 'closed_checked', 'dropped']).meta({ id: 'TaskState' });
export const Task = z
    .object({
    id: Uuid,
    title: z.string(),
    /** `null` = internal work, not for a client. */
    client: ClientRef.nullable(),
    /** `null` = "Not assigned". */
    assignee: UserRef.nullable(),
    due: IsoDate,
    /** The first due date, set on the first move; `null` if never moved. */
    orig_due: IsoDate.nullable(),
    priority: TaskPriority,
    source: TaskSource,
    state: TaskState,
    done: z.boolean(),
    done_at: Timestamp.nullable(),
    /** Derived: due before today (Asia/Kolkata) and not done. Never stored. */
    overdue: z.boolean(),
    days_overdue: z.number().int().nonnegative(),
    /** Set by a manager; `null` = default from priority (urgent → first, high → next, else later). */
    head_priority: HeadPriority.nullable(),
    /** The ordering actually used: `head_priority`, or its default. */
    effective_head_priority: HeadPriority,
    manual_order: z.number().int().nullable(),
    blocked: TaskBlock.nullable(),
    slips: z.array(Slip),
    slip_count: z.number().int().nonnegative(),
    /** Slipped 3 times or more → flagged to the associate director. */
    flagged: z.boolean(),
    closure: Closure.nullable(),
    /** The waiting change request, if any (the task stays with the member until it is decided). */
    change_request: ChangeRequest.nullable(),
    /** A client task (commitment) is waiting on this task (prototype chip "Client waiting"). */
    client_waiting: z.boolean(),
    origin_ref: OriginRef.nullable(),
    created_by: UserRef.nullable(),
    is_seed: z.boolean(),
    version: Version,
    created_at: Timestamp,
    updated_at: Timestamp,
})
    .meta({ id: 'Task' });
export const TaskIdParams = z.object({ id: Uuid });
export const TaskList = list(Task).meta({ id: 'TaskList' });
export const TaskPage = page(Task).meta({ id: 'TaskPage' });
// ---------------------------------------------------------------- shared list filters
const csvOf = (e) => z
    .string()
    .max(200)
    .transform((s, ctx) => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    const bad = parts.filter((p) => !e.safeParse(p).success);
    if (bad.length) {
        ctx.addIssue({ code: 'custom', message: `Unknown value(s): ${bad.join(', ')}. Use: ${e.options.join(', ')}` });
        return z.NEVER;
    }
    return parts;
});
export const WorkStatusFilter = z
    .enum(['open', 'overdue', 'blocked', 'proof', 'proposed', 'done', 'all'])
    .meta({ id: 'WorkStatusFilter' });
/** GET /tasks — tasks only. Same visibility as GET /work. */
export const ListTasksQuery = PageQuery.extend({
    /** Comma list; default `open`. `proof` = closed, proof not checked yet. */
    status: csvOf(WorkStatusFilter).optional(),
    /** Managers only (ignored for members). `none` = Not assigned. */
    assignee_id: z.union([Uuid, z.literal('none')]).optional(),
    client_id: Uuid.optional(),
    /** Matches title, case-insensitive. */
    q: z.string().trim().min(1).max(80).optional(),
});
// ---------------------------------------------------------------- POST /tasks
/**
 * Create one task, or one per ticked date (`dates`): N dates create N tasks titled
 * "{title} ({day label})", e.g. "Send the weekly report (Mon 5 Oct)".
 *
 * Who it goes to: only `task.reassign` (the founder and the associate director) may pick `assignee_id`. Anyone else gets
 * the default — the client's current SPOC for a client task, or themselves for internal work — and an
 * `assignee_id` that differs from that default is `403 forbidden` ("Ask for a change instead").
 * Repeating tasks (`repeat`) arrive with routines in build step 7.
 */
export const CreateTaskBody = z
    .object({
    title: z.string().trim().min(3, 'Say what needs doing in a few words').max(200),
    client_id: Uuid.optional(),
    assignee_id: Uuid.optional(),
    /** Ignored when `dates` is given. */
    due: IsoDate.optional(),
    /** One task per date; 1–31 distinct dates. */
    dates: z.array(IsoDate).min(1).max(31).optional(),
    priority: TaskPriority,
    source: TaskSource.exclude(['history']).default('adhoc'),
})
    .strict()
    .superRefine((b, ctx) => {
    if (!b.due && !b.dates)
        ctx.addIssue({ code: 'custom', path: ['due'], message: 'Pick a due date' });
    if (b.dates && new Set(b.dates).size !== b.dates.length) {
        ctx.addIssue({ code: 'custom', path: ['dates'], message: 'Each date can be ticked once' });
    }
});
// ---------------------------------------------------------------- PATCH /tasks/{id}
/** `head_priority` and `manual_order` need a manager (`task.reassign` or `tasks.read.all`); otherwise 403. */
export const UpdateTaskBody = z
    .object({
    title: z.string().trim().min(3).max(200).optional(),
    priority: TaskPriority.optional(),
    head_priority: HeadPriority.nullable().optional(),
    manual_order: z.number().int().min(0).max(10_000).nullable().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field' });
// ---------------------------------------------------------------- actions
/**
 * Move the due date. Moving it LATER appends a slip (and sets `orig_due` on the first move); moving it
 * earlier records no slip. At 3 slips the task is `flagged` ("flagged to the Ops associate director").
 * The reason is always required. Same date → 422.
 */
export const MoveDueBody = z
    .object({ due: IsoDate, reason_code: SlipReason, note: Note.optional() })
    .strict()
    .superRefine(needsNoteForOther);
export const BlockTaskBody = z
    .object({
    blocked_on: z.string().trim().min(2, 'Say who or what it is waiting on').max(120),
    note: Note.optional(),
})
    .strict();
/** `task.reassign` (the founder and the associate director). Members get 403 "Ask for a change instead". */
export const ReassignTaskBody = z
    .object({
    user_id: Uuid,
    reason: z.string().trim().min(3, 'Say why it is moving').max(500),
})
    .strict();
/**
 * Close with proof (guide §6.2). `note` needs `proof_text`; `link` needs `proof_url`. No proof → 422.
 * MIS tasks (`isMisTask`) also need `next_step`. Closing marks a linked open client task `kept`.
 */
export const CloseTaskBody = z
    .object({
    proof_kind: z.enum(['note', 'link']),
    proof_text: z.string().trim().min(3).max(2000).optional(),
    proof_url: z.url({ protocol: /^https?$/, message: 'Paste the full link, starting with https://' }).max(2000).optional(),
    proof_file_key: z.string().min(1).max(500).optional(),
    no_attachment_needed: z.boolean().optional(),
    next_step: z.string().trim().min(3).max(1000).optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.proof_kind === 'note' && !b.proof_text) {
        ctx.addIssue({ code: 'custom', path: ['proof_text'], message: 'Write what was done, so someone can check it' });
    }
    if (b.proof_kind === 'link' && !b.proof_url) {
        ctx.addIssue({ code: 'custom', path: ['proof_url'], message: 'Paste the link that shows it was done' });
    }
});
export const ReopenTaskBody = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
export const DropTaskBody = z
    .object({ reason_code: DropReason, note: Note.optional() })
    .strict()
    .superRefine(needsNoteForOther);
// ---------------------------------------------------------------- dropped items
export const DroppedSourceKind = z.enum(['mom', 'email', 'voice', 'request', 'task']).meta({ id: 'DroppedSourceKind' });
/** Prototype DSRC labels. */
export const DROPPED_SOURCE_LABEL = {
    mom: 'Meeting minutes',
    email: 'Email',
    voice: 'Voice note',
    request: 'Client request',
    task: 'Task list',
};
export const DroppedItem = z
    .object({
    id: Uuid,
    source_kind: DroppedSourceKind,
    /** e.g. "MOM: Q2 review, 4 Oct", or the task id. */
    source_ref: z.string().nullable(),
    client: ClientRef.nullable(),
    title: z.string(),
    assignee: UserRef.nullable(),
    due: IsoDate.nullable(),
    by: UserRef,
    at: Timestamp,
    reason_code: DropReason,
    note: z.string().nullable(),
    seen_by_manager: z.boolean(),
    state: z.enum(['open', 'restored']),
    /** The task created when it was restored. */
    restored_task_id: Uuid.nullable(),
})
    .meta({ id: 'DroppedItem' });
export const DroppedPage = page(DroppedItem).meta({ id: 'DroppedPage' });
export const DropResponse = z.object({ dropped_item: DroppedItem }).meta({ id: 'DropResponse' });
const BoolString = z.enum(['true', 'false']).transform((s) => s === 'true');
export const ListDroppedQuery = PageQuery.extend({
    /** `false` = only items no manager has marked seen. */
    seen: BoolString.optional(),
    state: z.enum(['open', 'restored']).optional(),
});
export const DroppedIdParams = z.object({ id: Uuid });
export const RestoreDroppedBody = z
    .object({
    /** Default: the original assignee (or the client's SPOC if that person is no longer active). */
    assignee_id: Uuid.optional(),
    /** Default: today, or the original due date if it is still ahead. */
    due: IsoDate.optional(),
})
    .strict();
export const MarkDroppedSeenBody = z.object({ ids: z.array(Uuid).min(1).max(200) }).strict();
// ---------------------------------------------------------------- change requests
export const ListChangeRequestsQuery = PageQuery.extend({ status: ChangeRequestStatus.optional() });
export const ChangeRequestPage = page(ChangeRequest).meta({ id: 'ChangeRequestPage' });
export const ChangeRequestIdParams = z.object({ id: Uuid });
/** Only the assignee, and only if they are not a manager (`changeRequest.raise`). One waiting at a time (409). */
export const RaiseChangeRequestBody = z
    .object({
    reason_code: ChangeReason,
    suggested_user_id: Uuid.optional(),
    suggested_due: IsoDate.optional(),
    note: Note.optional(),
})
    .strict()
    .superRefine(needsNoteForOther);
/**
 * `changeRequest.decide` (the associate director).
 * `reassign` needs `user_id`; `keep` needs `note` (said to the member); `remove` drops the task as a
 * duplicate/not needed (writes a dropped item with reason from the request).
 */
export const DecideChangeRequestBody = z
    .object({
    decision: ChangeDecision,
    user_id: Uuid.optional(),
    due: IsoDate.optional(),
    note: Note.optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.decision === 'reassign' && !b.user_id)
        ctx.addIssue({ code: 'custom', path: ['user_id'], message: 'Pick who it goes to' });
    if (b.decision === 'keep' && !b.note)
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Tell them why it stays with them' });
});
export const DecideChangeRequestResponse = z
    .object({
    change_request: ChangeRequest,
    /** The task after the decision; `null` when it was removed. */
    task: Task.nullable(),
    dropped_item: DroppedItem.nullable(),
})
    .meta({ id: 'DecideChangeRequestResponse' });
// ---------------------------------------------------------------- proof check
/**
 * Check a closure. Operator decision 12: the closer never checks their own proof (403); when the
 * associate director closed it, the founder checks (`proof.check.ad_closures`); otherwise the associate
 * director checks (`proof.check`). Acting-as does not get round this: the REAL person is compared.
 * `send_back` reopens the task, needs `note`, and creates an incident of kind `closure`.
 */
export const CheckClosureBody = z
    .object({ outcome: z.enum(['ok', 'send_back']), note: Note.optional() })
    .strict()
    .superRefine((b, ctx) => {
    if (b.outcome === 'send_back' && !b.note)
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say what is missing, so they can fix it' });
});
export const ClosureTaskParams = z.object({ task_id: Uuid });
export const TaskEnvelope = z.object({ task: Task }).meta({ id: 'TaskEnvelope' });
//# sourceMappingURL=tasks.js.map